import React, { useState, useEffect, memo, useCallback } from 'react';
import { useHoneyFarm } from '../../context/HoneyFarmContext';
import { fetchBee } from '../../helpers/fetchMetadata';

interface ILevelingBeeProps {
  address: string | undefined | null;
  isDesktop: boolean;
  beeId: number;
  selectedCombs: number[];
  txPending: boolean;
  setTxPending: (pending: boolean) => void;
}

export interface IMetadataTrait {
  trait_type: string;
  value: string;
}

export interface IIPFSBee {
  name: string;
  external_url: string;
  image: string;
  attributes: IMetadataTrait[];
  description: string;
}

const LevelingBee: React.FC<ILevelingBeeProps> = (props) => {
  const { levelUpBee, getBeeLevel } = useHoneyFarm();
  const [beeData, setBeeData] = useState<IIPFSBee>();
  const [beeLevel, setBeeLevel] = useState(-1);

  const loadBeeLevel = useCallback(async () => {
    if (!getBeeLevel || props.beeId <= 0) return;
    try {
      const level = await getBeeLevel(props.beeId);
      setBeeLevel(level);
    } catch (error) {
      console.error('Error loading bee level:', error);
      setBeeLevel(-1);
    }
  }, [getBeeLevel, props.beeId]);

  useEffect(() => {
    if (props.beeId > 0) {
      loadBeeLevel();

      fetchBee(props.beeId)
        .then((r: any) => {
          let response: IIPFSBee = r.data;
          setBeeData(response);
        })
        .catch((r: any) => {
          console.log(r, 'error');
        });
    }
  }, [props.beeId, loadBeeLevel]);

  useEffect(() => {
    if (props.beeId > 0 && props.txPending === false) {
      loadBeeLevel();
    }
  }, [props.txPending, props.beeId, loadBeeLevel]);

  const getTotalLevels = () => {
    let totalLevels = 0;
    props.selectedCombs.forEach((comb, index) => {
      let levelMultiplier = index + 1;
      totalLevels += (comb * levelMultiplier);
    });
    return totalLevels;
  };

  const levelBeeUp = async () => {
    let _quantities: any[] = [];
    let _honeyCombs: any[] = [];

    props.selectedCombs.forEach((c, index) => {
      if (c > 0) {
        _quantities.push(c);
        _honeyCombs.push(index);
      }
    });

    let reversed_q = _quantities.reverse();
    let reversed_h = _honeyCombs.reverse();

    if (!levelUpBee || reversed_h.length === 0) {
      return;
    }

    props.setTxPending(true);
    try {
      await levelUpBee(props.beeId, reversed_h, reversed_q);
      await loadBeeLevel();
    } catch (error) {
      console.error('Error leveling up bee:', error);
    } finally {
      props.setTxPending(false);
    }
  };

  return (
    <div className="bg-black bg-opacity-80 rounded-md shadow-lg overflow-hidden border border-[#ffa2f8] max-w-sm mx-auto mt-4">
      {/* Header */}
      <div className="bg-black bg-opacity-90 text-[#c4f6f9] text-sm font-bold p-4 text-center border-b border-[#ffa2f8]">
        Bees Deluxe #{props.beeId}
      </div>
      
      {/* Bee Image */}
      <div className="flex justify-center p-4">
        <img 
          src={beeData?.image} 
          alt={`Bee #${props.beeId}`}
          className="w-48 h-48 object-contain rounded-md border border-[#c4f6f9]"
          onError={(e) => {
            e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkyIiBoZWlnaHQ9IjE5MiIgdmlld0JveD0iMCAwIDE5MiAxOTIiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxOTIiIGhlaWdodD0iMTkyIiBmaWxsPSIjMzc0MTUxIi8+Cjx0ZXh0IHg9Ijk2IiB5PSIxMDgiIGZpbGw9IiNGRkYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtZmFtaWx5PSJtb25vc3BhY2UiIGZvbnQtc2l6ZT0iMTYiIGZvbnQtd2VpZ2h0PSJib2xkIj5CZWU8L3RleHQ+Cjwvc3ZnPg==';
          }}
        />
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Current Level */}
        {beeLevel > -1 && (
          <div className="text-center text-sm text-[#fcd34d] mb-4">
            Level {beeLevel}
          </div>
        )}

        {/* Action Area */}
        <div className="text-center">
          {/* No combs selected and not max level */}
          {getTotalLevels() === 0 && beeLevel < 5 && (
            <div className="text-sm text-[#55bac3]">
              Select honeycombs to level up
            </div>
          )}

          {/* Valid level up (won't exceed max) */}
          {getTotalLevels() > 0 && getTotalLevels() + beeLevel <= 5 && (
            <button
              className="px-6 py-3 bg-transparent border-2 border-[#55bac3] text-[#55bac3] hover:bg-[#55bac3] hover:text-black disabled:border-gray-600 disabled:text-gray-500 disabled:cursor-not-allowed rounded-md text-sm transition-colors"
              onClick={levelBeeUp}
              disabled={props.txPending}
            >
              Level Up x {getTotalLevels()}
            </button>
          )}

          {/* Level up would exceed max (show warning) */}
          {getTotalLevels() + beeLevel > 5 && beeLevel < 5 && (
            <div>
              <button
                className="px-6 py-3 bg-transparent border-2 border-[#55bac3] text-[#55bac3] hover:bg-[#55bac3] hover:text-black disabled:border-gray-600 disabled:text-gray-500 disabled:cursor-not-allowed rounded-md text-sm transition-colors mb-3"
                onClick={levelBeeUp}
                disabled={props.txPending}
              >
                Level Up x {getTotalLevels()}
              </button>
              <div className="text-red-400 font-bold text-sm">
                Caution: Additional <u>{getTotalLevels() + beeLevel - 5} levels</u> will not be added and honeycombs will be burnt
              </div>
            </div>
          )}

          {/* Already max level */}
          {beeLevel >= 5 && (
            <div className="text-sm text-[#fcd34d]">
              Bee at max level
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(LevelingBee, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.address === nextProps.address &&
    prevProps.isDesktop === nextProps.isDesktop &&
    prevProps.beeId === nextProps.beeId &&
    prevProps.selectedCombs === nextProps.selectedCombs &&
    prevProps.txPending === nextProps.txPending
  );
});